import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import {
  api,
  getPosHeaders,
} from "./api";

import { usePermissions } from "./PermissionContext";

import "./POSElectronicInvoice.css";
import POSInvoiceSettings from "./POSInvoiceSettings";

const STATUS_LABELS = {
  draft: "Borrador",
  pending: "Pendiente",
  processing: "En procesamiento",
  validated: "Validada",
  rejected: "Rechazada",
};


function errorMessage(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return "No fue posible consultar o preparar el documento.";
}


export default function POSElectronicInvoice({
  saleId,
  saleReference,
}) {
  const { company } = useParams();

  const { can } = usePermissions();

  const canView = can("billing.view");
  const canManage = can("billing.manage");

  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const mutationRef = useRef(false);
  const requestVersion = useRef(0);

  const validSaleId =
    Number.isSafeInteger(Number(saleId)) &&
    Number(saleId) > 0;

  // Invalida respuestas pendientes al desmontar el panel.
  useEffect(() => {
    return () => {
      requestVersion.current += 1;
    };
  }, []);

  const loadDocument = useCallback(
    async (signal) => {
      if (
        !validSaleId ||
        !canView ||
        mutationRef.current
      ) {
        return;
      }

      const version = ++requestVersion.current;

      setLoading(true);
      setError("");
      setMessage("");

      try {
        const response = await api.get(
          `/pos/electronic-invoices/by-sale/${Number(saleId)}`,
          {
            headers: getPosHeaders(company),
            signal,
          }
        );

        if (
          signal?.aborted ||
          version !== requestVersion.current
        ) {
          return;
        }

        setDocument(
          response.data.document || null
        );

        setMessage(
          response.data.message || ""
        );
      } catch (requestError) {
        if (
          signal?.aborted ||
          version !== requestVersion.current
        ) {
          return;
        }

        setError(errorMessage(requestError));
      } finally {
        if (
          !signal?.aborted &&
          version === requestVersion.current
        ) {
          setLoading(false);
        }
      }
    },
    [
      company,
      saleId,
      validSaleId,
      canView,
    ]
  );

  useEffect(() => {
    const controller = new AbortController();

    loadDocument(controller.signal);

    return () => controller.abort();
  }, [loadDocument]);

  async function prepareDocument() {
    if (
      !validSaleId ||
      !canManage ||
      loading ||
      mutationRef.current ||
      document
    ) {
      return;
    }

    mutationRef.current = true;

    const version = ++requestVersion.current;

    setPreparing(true);
    setError("");
    setMessage("");

    try {
      const response = await api.post(
        "/pos/electronic-invoices/from-sale",
        {
          sale_id: Number(saleId),
        },
        {
          headers: getPosHeaders(company),
        }
      );

      if (version !== requestVersion.current) {
        return;
      }

      setDocument(response.data.document);

      setMessage(
        response.data.message ||
        "Borrador electrónico preparado."
      );
    } catch (requestError) {
      if (version === requestVersion.current) {
        setError(errorMessage(requestError));
      }
    } finally {
      mutationRef.current = false;

      if (version === requestVersion.current) {
        setPreparing(false);
      }
    }
  }

  if (
    !validSaleId ||
    (!canView && !canManage)
  ) {
    return null;
  }

  const busy = loading || preparing;

  return (
    <section
      className="pos-electronic-card"
      aria-labelledby="pos-electronic-title"
      aria-busy={busy}
    >
      <header className="pos-electronic-heading">
        <div>
          <span className="pos-electronic-eyebrow">
            DOCUMENTO ELECTRÓNICO
          </span>

          <h2 id="pos-electronic-title">
            Facturación electrónica
          </h2>

          <p>
            Venta{" "}
            <strong>
              {saleReference || `#${saleId}`}
            </strong>
          </p>
        </div>

        <span className="pos-electronic-badge">
          {document
            ? STATUS_LABELS[document.status] ||
            document.status
            : "Sin preparar"}
        </span>
      </header>

      {loading && (
        <p role="status">
          Consultando documento…
        </p>
      )}

      {error && (
        <div
          className="pos-electronic-alert pos-electronic-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {message && (
        <p
          className="pos-electronic-message"
          role="status"
        >
          {message}
        </p>
      )}

      {document ? (
        <div className="pos-electronic-details">
          <div>
            <span>Referencia interna</span>
            <strong>
              Borrador #{document.id}
            </strong>
          </div>

          <div>
            <span>Ambiente</span>
            <strong>
              {document.environment === "production"
                ? "Producción"
                : "Pruebas"}
            </strong>
          </div>

          <div>
            <span>Conexión</span>
            <strong>
              {{
                provider: "Proveedor tecnológico",
                direct_dian: "Conexión directa DIAN",
                not_configured: "Sin configurar",
              }[document.connection_mode] ||
                document.connection_mode}
            </strong>
          </div>

          <div>
            <span>Número electrónico</span>
            <strong>
              {document.number
                ? `${document.prefix || ""}${document.number}`
                : "Sin asignar"}
            </strong>
          </div>
        </div>
      ) : (
        !loading && (

          <p className="pos-electronic-description">
            Prepara un borrador con los datos de esta
            venta. No consume numeración autorizada ni
            registra nuevamente pagos o inventario.
          </p>
        )
      )}

      <div className="pos-electronic-actions">
        {!document && canManage && (
          <button
            type="button"
            className="pos-electronic-primary"
            disabled={busy}
            onClick={prepareDocument}
          >
            {preparing
              ? "Preparando…"
              : "Preparar borrador electrónico"}
          </button>
        )}

        {canView && (
          <button
            type="button"
            className="pos-electronic-secondary"
            disabled={busy}
            onClick={() => loadDocument()}
          >
            Actualizar estado
          </button>
        )}
      </div>
      <POSInvoiceSettings />
      
      <p className="pos-electronic-note">
        La emisión electrónica está pendiente de
        integración. Este panel no transmite documentos
        ni acredita validación de la DIAN.
      </p>
    </section>
  );
}